// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import L from 'leaflet';
import type { ViewContext } from '../viewContext';
import { loadGeo, statesForYear, type StateAgg } from '../data';
import { jurisName } from '../domain';
import { RAMP, rampColour, rateColour, niceCeil } from '../components/charts';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatTonnes, formatPercent, fyShort } from '../utils/format';

type Mode = 'total' | 'perCapita' | 'recovery';
const MODES: Record<Mode, { label: string; title: string; sub: string; value: (s: StateAgg) => number | null; fmt: (v: number) => string; unit: string; diverging: boolean }> = {
  total: {
    label: 'Total waste',
    title: 'Total waste by state',
    sub: 'Raw tonnes generated in each state and territory — a map of population and industry as much as of waste. Click a state to open it.',
    value: (s) => s.total,
    fmt: (v) => formatTonnes(v),
    unit: 'tonnes',
    diverging: false,
  },
  perCapita: {
    label: 'Per person',
    title: 'Waste per resident',
    sub: 'Tonnes of waste per person — the honest denominator. Resource-state economies with big construction and mining sectors generate far more per head than their populations alone suggest.',
    value: (s) => s.perCapita,
    fmt: (v) => `${v.toFixed(1)} t`,
    unit: 'tonnes/person',
    diverging: false,
  },
  recovery: {
    label: 'Recovery rate',
    title: 'Resource recovery rate by state',
    sub: 'The share of waste kept out of landfill. Container-deposit and kerbside schemes, landfill levies and local reprocessing all move this — and it varies widely between states.',
    value: (s) => s.rate,
    fmt: (v) => `${v.toFixed(0)}%`,
    unit: '% recovered',
    diverging: true,
  },
};

const MAINLAND = L.latLngBounds([-44.5, 112], [-9.5, 154.5]);

export function renderMap(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const year = d.latestYear;
  let mode: Mode = (localStorage.getItem('waste.map.mode') as Mode) || 'recovery';
  if (!MODES[mode]) mode = 'recovery';
  const states = statesForYear(d, year);
  const byState = new Map(states.map((s) => [s.code, s]));

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The map of Australian waste</h1>
      <p class="view-sub">How each state and territory compares on total waste, waste per person, and
        ${glossaryTerm('recovery-rate', 'recovery rate')} in ${escapeHtml(fyShort(year))}. Hover for the numbers; click a state for its full breakdown.</p>
    </div>
    <div class="controls">
      <span class="field-label">Show</span>
      <div class="seg" role="group" data-role="mode">
        ${(Object.keys(MODES) as Mode[]).map((m) => `<button class="seg-btn" data-mode="${m}" aria-pressed="${m === mode}">${MODES[m].label}</button>`).join('')}
      </div>
    </div>
    <div class="panel">
      <div class="panel-title" data-role="title"></div>
      <div class="panel-sub" data-role="sub"></div>
      <div class="map-frame"><div class="map-canvas"></div></div>
      <div class="map-legend" data-role="legend"></div>
    </div>
  `;

  const canvas = root.querySelector('.map-canvas') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 9, scrollWheelZoom: false, preferCanvas: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Data: National Waste Report (DCCEEW) · Boundaries: ABS ASGS',
    subdomains: 'abcd', minZoom: 3, maxZoom: 9,
  }).addTo(map);
  map.fitBounds(MAINLAND, { padding: [6, 6] });

  const stopsFor = (m: Mode): number[] => {
    const vals = states.map((s) => MODES[m].value(s)).filter((v): v is number => v != null);
    const max = niceCeil(Math.max(...vals, 1));
    return [1, 2, 3, 4, 5, 6].map((i) => (max / 6) * i);
  };
  const colourFor = (code: string, m: Mode): string => {
    const s = byState.get(code);
    const v = s ? MODES[m].value(s) : null;
    if (v == null) return '#eef1f4';
    return MODES[m].diverging ? rateColour(v) : rampColour(v, stopsFor(m));
  };

  let polyLayer: L.GeoJSON | null = null;
  const styleState = (code: string) => ({ fillColor: colourFor(code, mode), fillOpacity: 0.85, color: '#ffffff', weight: 1 });

  const stateTip = (code: string): string => {
    const s = byState.get(code);
    if (!s || s.total === 0) return `<strong>${escapeHtml(jurisName(code))}</strong><br><span style="color:var(--text-tertiary)">No data</span>`;
    return (
      `<strong>${escapeHtml(jurisName(code))}</strong><br>` +
      `${formatTonnes(s.total)} · ${formatPercent(s.rate, 0)} recovered<br>` +
      `<span style="color:var(--text-secondary)">${s.perCapita != null ? `${s.perCapita.toFixed(1)} t/person` : '—'}</span>`
    );
  };

  const renderLegend = () => {
    const cfg = MODES[mode];
    if (cfg.diverging) {
      legendEl.innerHTML =
        `<span class="field-label">${escapeHtml(cfg.unit)}</span>` +
        `<span class="scale-bar">${['#c0503f', '#d1713a', '#e0902d', '#a9a13a', '#5f9647', '#2f8f4e'].map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span>` +
        `<span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">0% · 100%</span>`;
      return;
    }
    const stops = stopsFor(mode);
    legendEl.innerHTML =
      `<span class="field-label">${escapeHtml(cfg.unit)}</span>` +
      `<span class="scale-bar">${RAMP.map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span>` +
      `<span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">${[0, ...stops].map((v) => cfg.fmt(v)).join(' · ')}</span>`;
  };

  const paint = () => {
    const cfg = MODES[mode];
    titleEl.textContent = `${cfg.title} — ${fyShort(year)}`;
    subEl.textContent = cfg.sub;
    renderLegend();
    root.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-mode') === mode)));
    if (polyLayer) {
      polyLayer.setStyle((f: any) => styleState(f.properties.code));
      polyLayer.eachLayer((lyr: any) => lyr.setTooltipContent(stateTip(lyr.feature.properties.code)));
    }
  };

  root.querySelector('[data-role="mode"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-mode]');
    if (!btn) return;
    mode = btn.getAttribute('data-mode') as Mode;
    localStorage.setItem('waste.map.mode', mode);
    paint();
  });

  loadGeo()
    .then((geo) => {
      polyLayer = L.geoJSON(geo as any, {
        style: (f: any) => styleState(f.properties.code),
        onEachFeature: (f: any, lyr: L.Layer) => {
          const code = f.properties.code;
          lyr.bindTooltip(stateTip(code), { sticky: true, className: 'map-tip' });
          lyr.on({
            mouseover: () => (lyr as L.Path).setStyle({ weight: 2.4, color: '#1c2a1e' }),
            mouseout: () => polyLayer?.resetStyle(lyr as L.Path),
            click: () => ctx.openState(code),
          });
        },
      }).addTo(map);
      requestAnimationFrame(() => { map.invalidateSize(); map.fitBounds(MAINLAND, { padding: [6, 6] }); });
      paint();
    })
    .catch(() => { canvas.innerHTML = '<div class="error-box" style="margin:var(--space-lg)">Could not load the state boundaries.</div>'; });

  paint();
}
